import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
} from '@angular/core';
import Color from 'color';
import { BehaviorSubject, combineLatest, map } from 'rxjs';

import { Maybe } from '@plugins/shared/types/helpers';

import type { FlipVariants, IconSizes } from '../../public/types';
import { FLIP_CORRESPONDENCE } from '../constants';
import { ValidateAnimationTypeService } from '../services/validate-animation.service';
import {
  handleAnimationInput,
  handleDegInput,
  handleSizeInput,
} from '../utils/utils';

@Component({
  selector: 'icon-base-icon',
  imports: [CommonModule],
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseIconComponent {
  protected ref = inject<ElementRef<HTMLElement>>(ElementRef);
  @Input() set size(iconSize: Maybe<IconSizes>) {
    handleSizeInput(iconSize, this.#size, this.hostStyle);
  }
  @Input() iconStyle?: Record<string, string>;
  @Input() set color(colorVal: Maybe<string>) {
    this.hostStyle.setProperty(
      '--color',
      colorVal ? Color(colorVal).toString() : 'inherit',
    );
  }
  @Input() set rotate(angle: Maybe<number | string>) {
    if (angle === undefined || angle === null) return;
    const rotateVal = handleDegInput(angle);
    if (rotateVal) this.hostStyle.setProperty('--rotate', rotateVal);
  }
  @Input() set flip(flipVariant: Maybe<FlipVariants>) {
    if (!flipVariant) return;
    this.#flipClass.next(FLIP_CORRESPONDENCE[flipVariant]);
  }

  /** Provide animation configuration for the icon that satisfies the
   * following shape (only the animation name is required).
   *
   * `name-animateOn duration-num(ms|s) delay-num(ms|s) iterate-(num|infinite)
   * direction-(normal, reverse, alternate, alternate-reverse) | no-animation`
   *
   * If a nullish or incorrect value is supplied, no animations will be set.
   * */
  @Input() set animation(animationVariant: Maybe<string>) {
    handleAnimationInput(
      this.validateAnimationService.validate(animationVariant),
      this.#animationClass,
      this.hostStyle,
    );
  }

  protected validateAnimationService = inject(ValidateAnimationTypeService);

  #animationClass = new BehaviorSubject<string>('');
  #flipClass = new BehaviorSubject<string>('');
  #size = new BehaviorSubject<string>('md');
  containerClass$ = combineLatest([this.#size, this.#flipClass]).pipe(
    map((classes) => classes.join(' ')),
  );

  svgClass$ = combineLatest([this.#animationClass]).pipe(
    map((classes) => classes.join(' ')),
  );

  get hostStyle(): CSSStyleDeclaration {
    return this.ref.nativeElement.style;
  }
}
